/**
 * Small presentational chips shared by the two routing sections. All copy
 * comes from the rule/meta payloads - nothing tenant-specific lives here.
 */

import { MapPin, ShieldCheck, User, Users, Zap } from 'lucide-react'

import { Badge } from '@/shared/components/ui/badge'
import type {
  RoutingAssignee,
  RoutingRule,
  RoutingOption,
} from '@/admin/hooks/use-routing-rules'

const ASSIGNEE_ICON = { role: Users, person: User, zone: MapPin } as const

export function AssigneeChip({ assignee }: { assignee: RoutingAssignee }) {
  const Icon = ASSIGNEE_ICON[assignee.kind] ?? Users
  const label = assignee.label || assignee.value
  return (
    <Badge variant="outline" className="gap-1 font-normal">
      <Icon className="h-3 w-3 text-[var(--ink-3)]" />
      {assignee.kind === 'zone' ? `${label} (by zone)` : label}
    </Badge>
  )
}

export function ManagedBadge({ rule }: { rule: RoutingRule }) {
  if (!rule.managed_by) return null
  const label =
    rule.managed_by === 'approval_policy' ? 'Managed by Approvals' : 'Managed'
  return (
    <Badge variant="secondary" className="gap-1 font-normal">
      <ShieldCheck className="h-3 w-3 text-[var(--ink-3)]" />
      {label}
    </Badge>
  )
}

export function StrategyChip({
  strategy,
  strategyOptions,
}: {
  strategy: string
  strategyOptions: RoutingOption[]
}) {
  if (!strategy) return null
  const label =
    strategyOptions.find((option) => option.value === strategy)?.label ??
    strategy
  // Show only the short half of "Least busy - fewest open claims".
  const short = label.split(' - ')[0]
  return (
    <Badge variant="ghost" className="gap-1 font-normal text-[var(--ink-2)]">
      {short}
    </Badge>
  )
}

export function WorkflowScopeBadge({
  workflowKey,
  workflows,
}: {
  workflowKey: string
  workflows: { key: string; label: string }[]
}) {
  if (!workflowKey) return null
  const label = workflows.find((w) => w.key === workflowKey)?.label ?? workflowKey
  return (
    <Badge variant="ghost" className="gap-1 font-normal text-[var(--ink-3)]">
      {label}
    </Badge>
  )
}

export function ThenActionChip({ label }: { label: string }) {
  return (
    <Badge variant="outline" className="gap-1 font-normal">
      <Zap className="h-3 w-3 text-[var(--ink-3)]" />
      Then: {label}
    </Badge>
  )
}
